import React from 'react'
import { useQuery } from 'react-query'
import { Text, Button, Loader, HTMLText } from '@crossfit/ui'

import type { Image } from 'types'
import * as contentService from 'services/content'
import InnerContent from 'components/InnerContent'
import PageHeader from 'components/PageHeader'
import ImageGallery, { ImageGalleryImage } from 'components/ImageGallery'
import usePageTitle from 'hooks/usePageTitle'
import NotAuthorized from 'components/NotAuthorized'
import style from './style.module.css'
import { useProductAccess } from 'hooks/useProductAccess'
import { SubscriptionPackageType } from 'services/programming'
import { trackEvent } from 'services/segment'
import { useAffiliate } from 'hooks/useAffiliate'

type Content = {
  headlineText: string
  subheadlineText: string
  bodyText: string
  image: Image
  swoogoRedirectUrl: string
  swoogoRedirectText: string
  askBodyText: string
}

function useRoundtables() {
  const { canAccessRoundtables } = useAffiliate()
  const contentQuery = useQuery(
    'roundtables',
    () => contentService.fetchPage<Content>('/roundtables'),
    { enabled: canAccessRoundtables },
  )

  return {
    hasAccess: canAccessRoundtables,
    content: contentQuery.data,
    isLoading: contentQuery.isLoading,
  }
}

function RoundtablesPage() {
  const { content, isLoading, hasAccess } = useRoundtables()
  const { selectedAffiliate: affiliate } = useAffiliate()
  const productAccess = useProductAccess()

  usePageTitle(
    `CrossFit Affiliate Toolkit${content ? ` | ${content.headlineText}` : ''}`,
  )

  if (isLoading) {
    return <Loader />
  }

  if (!hasAccess) {
    return <NotAuthorized />
  }

  const optIn = () => {
    trackEvent({
      event: 'Roundtables Opt In Clicked',
      component: 'Roundtables Page',
      location: 'Affiliate Toolkit',
      linkUrl: content.swoogoRedirectUrl,
      text: content.swoogoRedirectText,
      affiliateId: affiliate?.affiliateId,
      userRole: affiliate?.roles?.length > 0 ? affiliate?.roles[0] : null,
      groupId: affiliate?.groupId,
    })
    location.href = content.swoogoRedirectUrl
  }

  return (
    <>
      <PageHeader subheadlineText={content.subheadlineText}>
        {content.headlineText}
      </PageHeader>
      <InnerContent className={style.innerContent} as='main' variant='xlarge'>
        <ImageGallery className={style.imageGallery}>
          <ImageGalleryImage {...content.image} />
        </ImageGallery>
        <div className={style.content}>
          <Text className={style.subHeading}></Text>
          <HTMLText className={style.htmlText}>
            {affiliate &&
            !productAccess.has(SubscriptionPackageType.ATK_STARTER)
              ? content.bodyText
              : content.askBodyText}
          </HTMLText>
          {affiliate &&
            !productAccess.has(SubscriptionPackageType.ATK_STARTER) && (
              <Button className={style.primaryButton} onClick={() => optIn()}>
                {content.swoogoRedirectText}
              </Button>
            )}
        </div>
      </InnerContent>
    </>
  )
}

export default RoundtablesPage
